import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { delay, finalize, map, of } from 'rxjs';
import moment from 'moment';
import { ChevronDownIconComponent } from '@kuiper/icons';
import { OverlayLoaderComponent } from '../../../../../common/components/overlay-loader/overlay-loader.component';
import { SectionHeaderComponent } from '../../../../../common/components/section-header/section-header.component';
import { ScrollToErrorDirective } from '../../../../../common/directives/scroll-to-error/scroll-to-error.directive';
import { EMPLOYEE_ROUTES } from '../../../../../common/constants/employee.routing.constants';
import { ENDPOINTS } from '../../../../../common/constants/endpoints.constants';
import { AppStorage } from '../../../../../common/enums/app-storage';
import { CookieService } from '../../../../../common/services/cookie/cookie.service';
import { FormService } from '../../../../../../form-builder/services/form/form.service';
import { filterInputTypesEnum } from '../../../../../../ui-components/enums/filter-input-type';
import { LookupFieldComponent } from '../../../../../../ui-components/lib/lookup-field/lookup-field.component';
import {
  AssignList,
  LookupFilter,
  LookupOptionItem,
} from '../../../../../../ui-components/lib/lookup-field/lookup-field-item';
import { MultiLookupFieldComponent } from '../../../../../../ui-components/lib/multi-lookup-field/multi-lookup-field.component';
import { DatePickerComponent } from '../../../../../../ui-components/lib/date-picker/date-picker.component';
import { RichEditorDualLangTextAreaComponent } from '../../../../../../ui-components/lib/rich-editor-dual-lang-text-area/rich-editor-dual-lang-text-area.component';
import { RadioButtonComponent } from '../../../../../../ui-components/lib/radio-button/radio-button.component';
import { ValidationMessageComponent } from '../../../../../../ui-components/lib/validation-message/validation-message.component';
import { ExpandCollapseAllComponent } from '../../../../../../ui-components/lib/expand-collapse-all/expand-collapse-all.component';
import { ToggleOption } from '../../../../../../ui-components/lib/option.interface';
import { ToasterService } from '../../../../../../ui-components/services/toast/toast.service';
import { Creator } from '../../../../../common/models/creator.model';
import { DelegationService } from '../delegation.service';
import {
  DelegationDetails,
  DelegationStatus,
  DelegationFormPayload,
  DelegationTaskScope,
  FlowTemplate,
  DualLanguageText,
} from '../delegation.model';
import {
  DELEGATION_TASK_SCOPE_OPTIONS,
  DELEGATION_USER_LOOKUP_ASSIGN_LIST,
  DELEGATION_WORKFLOW_LOOKUP_ASSIGN_LIST,
} from './delegation-form.constant';
import { dualLanguageCustomValidators } from '../../../../../../form-builder/services/form/custom-validators';
import { Status } from '../../../../../common/enums/user-status';

@Component({
  selector: 'lib-delegation-form',
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    ChevronDownIconComponent,
    OverlayLoaderComponent,
    SectionHeaderComponent,
    ScrollToErrorDirective,
    LookupFieldComponent,
    MultiLookupFieldComponent,
    DatePickerComponent,
    RichEditorDualLangTextAreaComponent,
    RadioButtonComponent,
    ValidationMessageComponent,
    ExpandCollapseAllComponent,
  ],
  templateUrl: './delegation-form.component.html',
  styleUrl: './delegation-form.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DelegationFormComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);
  private readonly formService = inject(FormService);
  private readonly toaster = inject(ToasterService);
  private readonly cookieService = inject(CookieService);
  private readonly delegationService = inject(DelegationService);

  protected readonly EMPLOYEE_ROUTES = EMPLOYEE_ROUTES;
  protected readonly userLookupAssignList = signal<AssignList>({
    ...DELEGATION_USER_LOOKUP_ASSIGN_LIST,
    extraLabels: DELEGATION_USER_LOOKUP_ASSIGN_LIST.extraLabels.map(item => ({
      ...item,
      label: this.transloco.translate(item.label),
    })),
  });

  protected readonly workflowLookupAssignList = signal<AssignList>({
    ...DELEGATION_WORKFLOW_LOOKUP_ASSIGN_LIST,
    extraLabels: DELEGATION_WORKFLOW_LOOKUP_ASSIGN_LIST.extraLabels.map(
      item => ({
        ...item,
        label: this.transloco.translate(item.label),
      })
    ),
  });
  protected readonly userLookupApi = `${ENDPOINTS.Lookup.User}?filters[not]=${this.loggedInUserId}&filters[not_states][0]=${Status.INACTIVE}&filters[not_states][1]=${Status.CANCELLED}&filters[not_states][2]=${Status.DELETED}&filters[not_delegated_by][delegator_id]=${this.loggedInUserId}&filters[not_delegated_by][delegate_id]=${this.loggedInUserId}`;

  protected readonly lookupSearchFilters = signal<LookupFilter[]>([
    {
      key: 'search',
      value: null,
      value_type: filterInputTypesEnum.STATIC,
      multiple: false,
      required: false,
    },
  ]);

  protected readonly workflowLookupSearchFilters = signal<LookupFilter[]>([
    {
      key: 'keyword',
      value: null,
      value_type: filterInputTypesEnum.STATIC,
      multiple: false,
      required: false,
    },
  ]);

  readonly scrollToError = viewChild.required(ScrollToErrorDirective);

  readonly isLoading = signal(true);
  readonly isSubmitting = signal(false);
  readonly editMode = signal(false);
  readonly isAllSectionsExpanded = signal(true);
  readonly selectedDelegatee = signal<LookupOptionItem | undefined>(undefined);
  readonly selectedWorkflows = signal<LookupOptionItem[]>([]);
  readonly periodFromMinDate = signal(this.startOfToday());
  private readonly delegationState = signal<DelegationStatus | null>(null);
  readonly isStartedAtDisabled = computed(
    () => this.delegationState() === DelegationStatus.ACTIVE
  );

  readonly pageTitle = computed(() =>
    this.transloco.translate(
      this.editMode()
        ? 'delegation_form_screen_edit_title'
        : 'delegation_form_screen_add_title'
    )
  );
  readonly submitButtonLabel = computed(() =>
    this.transloco.translate(
      this.editMode()
        ? 'delegation_form_screen_save_button'
        : 'delegation_form_screen_create_button'
    )
  );
  readonly taskScopeOptions = computed<ToggleOption[]>(() =>
    DELEGATION_TASK_SCOPE_OPTIONS.map(option => ({
      value: option.value,
      label: this.transloco.translate(option.label),
      description: this.transloco.translate(option.description),
    }))
  );

  readonly delegationForm = new FormGroup({
    delegate_id: new FormControl<number | null>(null, Validators.required),
    started_at: new FormControl<string | null>(null, Validators.required),
    ended_at: new FormControl<string | null>(null, [
      Validators.required,
      periodToMinDateValidator,
    ]),
    reason: new FormGroup(
      {
        en: new FormControl<string | null>(null),
        ar: new FormControl<string | null>(null),
      },
      { validators: [dualLanguageCustomValidators.atLeastOneLangRequired] }
    ),
    flow_template_ids: new FormArray<FormControl<number | null>>([]),
    task_scope: new FormControl<DelegationTaskScope>(
      DelegationTaskScope.DIRECT,
      Validators.required
    ),
  });

  private readonly periodFromValue = toSignal(
    this.delegationForm.controls.started_at.valueChanges,
    { initialValue: this.delegationForm.controls.started_at.value }
  );

  private readonly selectedFlowTemplateIds = toSignal(
    this.delegationForm.valueChanges.pipe(
      map(value => value.flow_template_ids ?? [])
    ),
    { initialValue: this.delegationForm.controls.flow_template_ids.value }
  );

  protected readonly workflowLookupApi = computed(() => {
    const notIds = (this.selectedFlowTemplateIds() ?? [])
      .filter((id): id is number => id != null)
      .map(id => String(id));
    const notIdsFilter =
      notIds.length > 0
        ? notIds
            .map((id, index) => `&filters[not_ids][${index}]=${id}`)
            .join('')
        : '';

    return (
      ENDPOINTS.Lookup.FlowTemplates +
      `?filters[delegatable]=1&filters[can_delegate_by_user]=${this.loggedInUserId}&sort[category][type]=asc${notIdsFilter}`
    );
  });

  readonly periodToMinDate = computed(() => {
    const startAt = this.periodFromValue();
    return startAt ? this.startOfNextDay(startAt) : this.periodFromMinDate();
  });

  constructor() {
    effect(() => {
      this.periodFromValue();
      untracked(() => this.revalidatePeriodTo());
    });
  }

  ngOnInit(): void {
    this.initializeMode();
  }

  private get loggedInUserId(): string {
    const authData = JSON.parse(
      this.cookieService.get(AppStorage.centralUser) || '{}'
    ) as { id?: number | string };

    return String(authData.id ?? '');
  }

  goBack(): void {
    history.back();
  }

  submit(): void {
    this.formService.syncDualLangControls(this.delegationForm);
    this.delegationForm.markAllAsTouched();

    if (this.delegationForm.invalid) {
      this.scrollToError().scrollToFirstError();
      return;
    }

    this.isSubmitting.set(true);
    const payload = this.buildPayload();
    const request$ = this.editMode()
      ? this.delegationService.updateDelegation(
          this.route.snapshot.params['id'],
          payload
        )
      : this.delegationService.createDelegation(payload);

    request$
      .pipe(
        finalize(() => this.isSubmitting.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => this.handleSubmitSuccess(),
        error: (error: {
          errors: { [key: string]: string[] };
          message: string;
        }) => this.handleSubmitError(error),
      });
  }

  private initializeMode(): void {
    const delegationId = this.route.snapshot.params['id'];
    if (!delegationId) {
      this.editMode.set(false);
      this.isLoading.set(false);
      return;
    }

    this.editMode.set(true);
    this.delegationService
      .getDelegation(delegationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => this.patchForm(res.data),
        error: () => {
          this.isLoading.set(false);
          this.goBack();
        },
      });
  }

  private patchForm(details: DelegationDetails): void {
    this.selectedDelegatee.set(this.toLookupOption(details.delegate));
    this.selectedWorkflows.set(
      (details.flow_templates ?? [])
        .map(workflow => this.toWorkflowLookupOption(workflow))
        .filter((item): item is LookupOptionItem => !!item)
    );
    const flowTemplateIds =
      details.flow_templates?.map(workflow => workflow.id ?? null) ?? [];
    this.delegationForm.patchValue({
      delegate_id: details.delegate?.id ?? null,
      started_at: details.started_at ?? null,
      ended_at: details.ended_at ?? null,
      reason:
        details.translations?.reason &&
        !Array.isArray(details.translations.reason)
          ? details.translations.reason
          : { en: null, ar: null },
      flow_template_ids: flowTemplateIds,
      task_scope: details.task_scope ?? DelegationTaskScope.DIRECT,
    });
    this.delegationState.set(details.state?.value ?? null);
    this.syncStartedAtDisabledState();
    this.isLoading.set(false);
  }

  private syncStartedAtDisabledState(): void {
    const startedAtControl = this.delegationForm.controls.started_at;
    if (this.isStartedAtDisabled()) {
      startedAtControl.disable();
      return;
    }

    startedAtControl.enable();
  }

  private buildPayload(): DelegationFormPayload {
    const formValue = this.delegationForm.getRawValue();
    return {
      delegate_id: Number(formValue.delegate_id),
      started_at: this.isStartedAtDisabled()
        ? null
        : this.toUtcMorningTimestamp(formValue.started_at),
      ended_at: this.toUtcMorningTimestamp(formValue.ended_at),
      reason: formValue.reason as DualLanguageText,
      flow_template_ids: (formValue.flow_template_ids ?? [])
        .filter((id): id is number => id != null)
        .map(Number),
      task_scope: formValue.task_scope ?? DelegationTaskScope.DIRECT,
    };
  }

  private toUtcMorningTimestamp(value: string | null): string {
    return moment(value)
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      .format('DD-MM-YYYY hh:mm A');
  }

  private handleSubmitSuccess(): void {
    this.toaster.add(
      this.transloco.translate(
        this.editMode()
          ? 'delegation_form_screen_update_success_message'
          : 'delegation_form_screen_create_success_message'
      ),
      this.transloco.translate(
        this.editMode()
          ? 'delegation_form_screen_update_success_message_description'
          : 'delegation_form_screen_create_success_message_description'
      ),
      '',
      'success',
      true,
      true
    );
    this.goBack();
  }

  private handleSubmitError(error: {
    errors: { [key: string]: string[] };
    message: string;
  }): void {
    this.formService.addControlErrors(error, this.delegationForm);
    of(null)
      .pipe(delay(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.scrollToError().scrollToFirstError(false));
    this.toaster.add(
      this.transloco.translate('delegation_form_screen_error_message'),
      this.transloco.translate(
        'delegation_form_screen_error_message_description'
      ),
      '',
      'error',
      true,
      true
    );
  }

  private toLookupOption(
    item: Creator | LookupOptionItem
  ): LookupOptionItem | undefined {
    if (!item) {
      return undefined;
    }

    const value = 'value' in item && item.value != null ? item.value : item.id;
    const label =
      'label' in item && item.label ? item.label : (item as Creator).name;

    if (value == null || !label) {
      return undefined;
    }

    return {
      value,
      label,
      image: item.image ?? undefined,
    };
  }

  private toWorkflowLookupOption(
    item: FlowTemplate
  ): LookupOptionItem | undefined {
    if (!item) {
      return undefined;
    }

    const id = item.id;
    const name = item.name;

    if (id == null || !name) {
      return undefined;
    }

    return {
      value: id,
      label: name,
      id,
      name,
      image: item.icon ?? undefined,
      category: item.category ?? undefined,
      description: item.description ?? undefined,
    };
  }

  private revalidatePeriodTo(): void {
    const endControl = this.delegationForm.controls.ended_at;
    endControl.updateValueAndValidity();

    if (endControl.hasError('minDateTime')) {
      endControl.markAsTouched();
    }
  }

  private startOfToday(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  private startOfNextDay(date: string): Date {
    return moment(date).add(1, 'day').startOf('day').toDate();
  }
}

function periodToMinDateValidator(
  control: AbstractControl
): ValidationErrors | null {
  const startAt = control.parent?.get('started_at')?.value as string | null;
  const endAt = control.value as string | null;

  if (!startAt || !endAt || !moment(endAt).isBefore(moment(startAt))) {
    return null;
  }

  return {
    minDateTime: {
      minDate: true,
      date: startAt,
    },
  };
}
